import React, { useEffect } from 'react';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';

export default function RefundPolicyPage() {
  useEffect(() => {
    document.title = "Refund & Returns Policy | Campus Market";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <CampusHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-600 via-indigo-500 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-white/20">Policy</span>
            <span className="text-white/80 text-sm">Paystack‑friendly</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Refund & Returns Policy</h1>
          <p className="mt-3 max-w-3xl text-white/90">
            Clear, fair rules for returns, refunds, and dispute resolution on Campus Market.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1">
              <div className="bg-white border rounded-2xl shadow-sm p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">On this page</h2>
                <nav className="space-y-2 text-sm">
                  <a href="#eligibility" className="block text-gray-700 hover:text-indigo-600">Eligibility</a>
                  <a href="#conditions" className="block text-gray-700 hover:text-indigo-600">Conditions</a>
                  <a href="#process" className="block text-gray-700 hover:text-indigo-600">Process</a>
                  <a href="#nonref" className="block text-gray-700 hover:text-indigo-600">Non‑Refundable Items</a>
                  <a href="#seller" className="block text-gray-700 hover:text-indigo-600">Seller Obligations</a>
                  <a href="#contact" className="block text-gray-700 hover:text-indigo-600">Contact</a>
                </nav>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-8">
              <section id="eligibility" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-checkbox-circle-line text-indigo-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Eligibility</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Requests must be made within 7 days of delivery/collection, unless specified otherwise by the seller.</li>
                  <li>Refunds apply where items are not as described, defective, counterfeit, or not delivered.</li>
                  <li>Digital goods may have distinct terms; check listing details.</li>
                </ul>
              </section>

              <section id="conditions" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-file-check-line text-teal-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Conditions</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Item must be returned in original condition and packaging unless defective.</li>
                  <li>Proof of purchase and reasonable evidence (photos, videos) may be required.</li>
                  <li>Return shipping or drop‑off is buyer’s responsibility unless the item is defective or misrepresented.</li>
                </ul>
              </section>

              <section id="process" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-time-line text-amber-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Refund Process</h2>
                </div>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>Contact the seller through Campus Market messages to request a return/refund.</li>
                  <li>If unresolved, escalate via <a href="mailto:support@synccore.com" className="text-indigo-600 underline">support@synccore.com</a>.</li>
                  <li>Approved refunds are processed via the original payment method (e.g., Paystack) within 5–10 business days.</li>
                </ol>
              </section>

              <section id="nonref" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-prohibited-line text-red-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Non‑Refundable Items</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Perishables, hygiene products, or items specified as non‑returnable in the listing.</li>
                  <li>Services completed or time‑based bookings that have been fulfilled.</li>
                  <li>Items damaged by misuse or not returned in the required condition.</li>
                </ul>
              </section>

              <section id="seller" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-store-2-line text-purple-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Seller Obligations</h2>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Accurately describe items; honor reasonable return/refund requests under this policy.</li>
                  <li>Respond to refund requests within 48 hours and cooperate in resolution.</li>
                  <li>Comply with Paystack rules and applicable consumer protection laws.</li>
                </ul>
              </section>

              <section id="contact" className="bg-white border rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-customer-service-2-line text-teal-600"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Contact & Support</h2>
                </div>
                <p className="text-gray-700">
                  For disputes, refunds, or returns, contact the seller first. If unresolved, email
                  <a href="mailto:support@synccore.com" className="text-indigo-600 underline"> support@synccore.com</a>.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}